import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, BrowserRouter, Routes } from 'react-router-dom';
import { authApi } from './services/api';
import { LoginPage } from './pages/LoginPage';
import { MfaVerifyPage } from './pages/MfaVerifyPage';
import { MfaSetupPage } from './pages/MfaSetupPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProspectsPage } from './pages/ProspectsPage';
import { ProspectDetailPage } from './pages/ProspectDetailPage';
import { PipelinePage } from './pages/PipelinePage';
import { OpportunityDetailPage } from './pages/OpportunityDetailPage';
import { CalendarPage } from './pages/CalendarPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { ActivityPage } from './pages/ActivityPage';
import { KpiDirectionPage } from './pages/KpiDirectionPage';
import { ConfigPage } from './pages/ConfigPage';
import { MapPage } from './pages/MapPage';
import { AuditPage } from './pages/AuditPage';
import { ApporteursPage } from './pages/ApporteursPage';
import { ClientJeopardyPage } from './pages/ClientJeopardyPage';
import { SecurityPage } from './pages/SecurityPage';
import { ToastProvider } from './components/ui/Toast';
import { useAuthStore } from './stores/auth';
import { useBrandBootstrap } from './hooks/useBrandBootstrap';

function Protected({ children }: { children: React.ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const passwordChangeRequired = useAuthStore((s) => s.passwordChangeRequired);
  if (!accessToken) return <Navigate to="/login" replace />;
  // Si le backend a signalé `PASSWORD_CHANGE_REQUIRED` sur une requête
  // précédente, on redirige vers l'écran dédié avant tout rendu métier.
  // L'utilisateur ne peut pas consulter le CRM tant que le mot de passe
  // temporaire n'est pas remplacé (SECURITY.md §1.2.1).
  if (passwordChangeRequired) return <Navigate to="/password/change" replace />;
  return <>{children}</>;
}

export function App() {
  // Expose le store d'auth sur `window` pour que `services/api.ts` puisse
  // poser le flag `passwordChangeRequired` sans import circulaire lorsqu'il
  // intercepte une réponse 403 PASSWORD_CHANGE_REQUIRED.
  const setPasswordChangeRequired = useAuthStore((s) => s.setPasswordChangeRequired);
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const clearAuth = useAuthStore((s) => s.clear);
  const accessToken = useAuthStore((s) => s.accessToken);
  // Applique la couleur de marque + favicon personnalisés au boot.
  useBrandBootstrap(accessToken);
  useEffect(() => {
    // Pont entre le store Zustand et `services/api.ts` (qui ne peut pas
    // importer le store directement sans créer un cycle). Expose les
    // setters + un getter dynamique du access token pour l'intercepteur
    // 401 → silent refresh → rejeu (cf api.ts::doSilentRefresh).
    (window as unknown as { __crmAuthStore?: unknown }).__crmAuthStore = {
      setPasswordChangeRequired,
      setAuthenticated,
      clear: clearAuth,
      getAccessToken: () => useAuthStore.getState().accessToken,
    };
    return () => {
      delete (window as unknown as { __crmAuthStore?: unknown }).__crmAuthStore;
    };
  }, [setPasswordChangeRequired, setAuthenticated, clearAuth]);

  // Bootstrap d'auth : grâce à la persistance localStorage du store, un
  // nouvel onglet récupère déjà l'access token au mount. On vérifie juste
  // qu'il n'est pas expiré (decode JWT.exp) - sinon on tente un /refresh.
  // Sans cette persistance, chaque nouvel onglet appelait /refresh, et les
  // appels concurrents déclenchaient l'anti-rejeu côté backend → toutes les
  // sessions révoquées (cf docs/SECURITY.md anti-vol refresh).
  const [bootstrapped, setBootstrapped] = useState(
    () => Boolean(accessToken) && isAccessTokenStillValid(accessToken),
  );
  // Guard StrictMode : React 18 fire les effets DEUX fois en dev. Sans ce
  // ref, deux /refresh partent en parallèle avec le même RT cookie → le 2ème
  // déclenche l'anti-vol côté backend → révoque TOUTES les sessions.
  const bootstrapStarted = useRef(false);
  useEffect(() => {
    if (bootstrapped || bootstrapStarted.current) return;
    bootstrapStarted.current = true;
    // Pas de `cancelled` flag ni de cleanup : le ref `bootstrapStarted`
    // suffit pour empêcher le double appel sous StrictMode. Si on posait
    // `cancelled = true` dans la cleanup du 1er effect, on perdrait la
    // résolution de la promise (le 2ème effect est skippé par le ref, mais
    // la 1ère promise verrait son `setBootstrapped` ignoré → spinner infini).
    authApi
      .refresh()
      .then(setAuthenticated)
      .catch(() => {
        // Refresh échoué (cookie invalide / session révoquée / périmée).
        // On NETTOIE le store : sinon un accessToken stale reste en
        // localStorage et `<Protected>` le voit comme valide → laisse passer
        // → toutes les API renvoient 401 au lieu de rediriger vers /login.
        clearAuth();
      })
      .finally(() => setBootstrapped(true));
    // Volontairement uniquement au mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!bootstrapped) return <BootstrapSpinner />;

  return (
    <BrowserRouter>
      <ToastProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/mfa/verify" element={<MfaVerifyPage />} />
          <Route path="/mfa/setup" element={<MfaSetupPage />} />
          <Route path="/password/change" element={<ChangePasswordPage />} />
          <Route
            path="/"
            element={
              <Protected>
                <DashboardPage />
              </Protected>
            }
          />
          <Route
            path="/prospects"
            element={
              <Protected>
                <ProspectsPage />
              </Protected>
            }
          />
          <Route
            path="/prospects/:id"
            element={
              <Protected>
                <ProspectDetailPage />
              </Protected>
            }
          />
          <Route
            path="/pipeline"
            element={
              <Protected>
                <PipelinePage />
              </Protected>
            }
          />
          <Route
            path="/opportunities/:id"
            element={
              <Protected>
                <OpportunityDetailPage />
              </Protected>
            }
          />
          <Route
            path="/calendar"
            element={
              <Protected>
                <CalendarPage />
              </Protected>
            }
          />
          <Route
            path="/notifications"
            element={
              <Protected>
                <NotificationsPage />
              </Protected>
            }
          />
          <Route
            path="/activity"
            element={
              <Protected>
                <ActivityPage />
              </Protected>
            }
          />
          <Route
            path="/kpi"
            element={
              <Protected>
                <KpiDirectionPage />
              </Protected>
            }
          />
          {/* Redirector de rétrocompat (bookmarks) : l'ancienne page
              dédiée a été fusionnée dans le tab Adélie de /kpi. */}
          <Route path="/kpi-consolides" element={<Navigate to="/kpi?tab=adelie" replace />} />
          <Route
            path="/config"
            element={
              <Protected>
                <ConfigPage />
              </Protected>
            }
          />
          <Route
            path="/security"
            element={
              <Protected>
                <SecurityPage />
              </Protected>
            }
          />
          <Route
            path="/map"
            element={
              <Protected>
                <MapPage />
              </Protected>
            }
          />
          <Route
            path="/audit"
            element={
              <Protected>
                <AuditPage />
              </Protected>
            }
          />
          <Route
            path="/apporteurs"
            element={
              <Protected>
                <ApporteursPage />
              </Protected>
            }
          />
          <Route
            path="/jeopardy"
            element={
              <Protected>
                <ClientJeopardyPage />
              </Protected>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ToastProvider>
    </BrowserRouter>
  );
}

/**
 * Décode l'`exp` d'un JWT (sans vérification de signature - la validation
 * réelle se fait côté serveur). Retourne `false` si expiré, malformé, ou
 * proche de l'expiration (buffer 30s pour éviter une 401 en pleine requête).
 */
function isAccessTokenStillValid(token: string | null): boolean {
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) return false;
    const payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(payloadB64)) as { exp?: number };
    const now = Math.floor(Date.now() / 1000);
    return typeof payload.exp === 'number' && payload.exp > now + 30;
  } catch {
    return false;
  }
}

function BootstrapSpinner() {
  return (
    <div className="flex h-[calc(100vh/var(--app-zoom))] items-center justify-center bg-bg-page">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-border-subtle border-t-accent"
        role="status"
        aria-label="Chargement…"
      />
    </div>
  );
}
